/**
 * EPIs da empresa (#551, spec D17, D19 e D21): o que a empresa entrega aos funcionários, com os
 * C.A. que ela mesma mantém e uma orientação de uso própria opcional.
 *
 * Cada ação de escrita tem a permissão dela (criar, editar, remover) e some para quem não a tem.
 * Depois de cada gravação a lista é lida de novo: o servidor é quem sabe o que ficou.
 */
import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  HealthAndSafety as EpiIcon,
} from '@mui/icons-material';
import { PrerequisiteBlock, ProfileSection } from '../../../../components/common';
import { useAcao } from '../../../../authorization/useVeredito';
import { useListaPermitida } from '../../../../authorization/useListaPermitida';
import { companyEpiService } from '../../../../services/companyEpiService';
import { epiService, EpiStatus, type Epi } from '../../../../services/epiService';
import { getApiErrorMessage } from '../../../../types/common';
import {
  certificadoVencido,
  formatarValidade,
  type CertificateInput,
  type CompanyEpi,
  type CompanyEpiCertificate,
} from '../../../../types/companyEpi';

interface CompanyEpisSectionProps {
  companyId: string;
}

/** O que o diálogo aberto está fazendo; só um existe por vez. */
type Dialogo =
  | { tipo: 'adicionar-epi' }
  | { tipo: 'certificado'; epi: CompanyEpi; certificado: CompanyEpiCertificate | null }
  | { tipo: 'orientacao'; epi: CompanyEpi }
  | { tipo: 'remover-epi'; epi: CompanyEpi }
  | { tipo: 'remover-certificado'; epi: CompanyEpi; certificado: CompanyEpiCertificate };

const LIMITE_DO_CADASTRO_BASE = 1000;

const DialogoDeEpi: React.FC<{
  epis: readonly Epi[];
  ocupado: boolean;
  erro: string | null;
  onConfirmar: (epiId: string) => void;
  onFechar: () => void;
}> = ({ epis, ocupado, erro, onConfirmar, onFechar }) => {
  const [epiId, setEpiId] = useState('');
  return (
    <Dialog open onClose={onFechar} fullWidth maxWidth="sm">
      <DialogTitle>Adicionar EPI</DialogTitle>
      <DialogContent>
        {erro && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {erro}
          </Alert>
        )}
        <TextField
          select
          fullWidth
          label="EPI"
          value={epiId}
          onChange={(evento) => setEpiId(evento.target.value)}
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          sx={{ mt: 1 }}
        >
          <option value="">Escolha um EPI do cadastro</option>
          {epis.map((epi) => (
            <option key={epi.id} value={String(epi.id)}>
              {epi.nome}
            </option>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar}>Cancelar</Button>
        <Button variant="contained" disabled={!epiId || ocupado} onClick={() => onConfirmar(epiId)}>
          Adicionar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DialogoDeCertificado: React.FC<{
  certificado: CompanyEpiCertificate | null;
  ocupado: boolean;
  erro: string | null;
  onConfirmar: (dados: CertificateInput) => void;
  onFechar: () => void;
}> = ({ certificado, ocupado, erro, onConfirmar, onFechar }) => {
  const [numero, setNumero] = useState(certificado?.numero ?? '');
  const [validade, setValidade] = useState(certificado?.validade.slice(0, 10) ?? '');
  return (
    <Dialog open onClose={onFechar} fullWidth maxWidth="xs">
      <DialogTitle>{certificado ? 'Trocar C.A.' : 'Adicionar C.A.'}</DialogTitle>
      <DialogContent>
        {erro && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {erro}
          </Alert>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Número do C.A."
            value={numero}
            onChange={(evento) => setNumero(evento.target.value)}
            inputProps={{ maxLength: 30 }}
            fullWidth
          />
          <TextField
            label="Validade"
            type="date"
            value={validade}
            onChange={(evento) => setValidade(evento.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={!numero.trim() || !validade || ocupado}
          onClick={() => onConfirmar({ numero: numero.trim(), validade })}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DialogoDeOrientacao: React.FC<{
  epi: CompanyEpi;
  ocupado: boolean;
  erro: string | null;
  onConfirmar: (orientacao: string | null) => void;
  onFechar: () => void;
}> = ({ epi, ocupado, erro, onConfirmar, onFechar }) => {
  const [texto, setTexto] = useState(epi.orientacaoDeUso ?? epi.orientacaoDoCadastro ?? '');
  return (
    <Dialog open onClose={onFechar} fullWidth maxWidth="sm">
      <DialogTitle>Orientação de uso · {epi.epiNome}</DialogTitle>
      <DialogContent>
        {erro && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {erro}
          </Alert>
        )}
        <TextField
          label="Orientação de uso"
          value={texto}
          onChange={(evento) => setTexto(evento.target.value)}
          multiline
          minRows={4}
          fullWidth
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar}>Cancelar</Button>
        {epi.orientacaoDeUso !== null && (
          <Button disabled={ocupado} onClick={() => onConfirmar(null)}>
            Voltar para a do cadastro-base
          </Button>
        )}
        <Button variant="contained" disabled={ocupado} onClick={() => onConfirmar(texto)}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DialogoDeRemocao: React.FC<{
  titulo: string;
  ocupado: boolean;
  erro: string | null;
  onConfirmar: () => void;
  onFechar: () => void;
}> = ({ titulo, ocupado, erro, onConfirmar, onFechar }) => (
  <Dialog open onClose={onFechar} fullWidth maxWidth="xs">
    <DialogTitle>{titulo}</DialogTitle>
    {erro && (
      <DialogContent>
        <Alert severity="error">{erro}</Alert>
      </DialogContent>
    )}
    <DialogActions>
      <Button onClick={onFechar}>Cancelar</Button>
      <Button color="error" variant="contained" disabled={ocupado} onClick={onConfirmar}>
        Confirmar remoção
      </Button>
    </DialogActions>
  </Dialog>
);

const CompanyEpisSection: React.FC<CompanyEpisSectionProps> = ({ companyId }) => {
  const adicionar = useAcao('company_epi.create', 'Adicionar EPI');
  const editar = useAcao('company_epi.update', 'Editar');
  const remover = useAcao('company_epi.delete', 'Remover');

  const carregarEpis = useCallback(() => companyEpiService.list(companyId), [companyId]);
  const carregarCadastro = useCallback(
    async () => (await epiService.getEpis({ ativo: EpiStatus.ATIVO, limit: LIMITE_DO_CADASTRO_BASE })).epis,
    []
  );

  const daEmpresa = useListaPermitida<CompanyEpi>('company_epi.read', 'EPIs da empresa', carregarEpis);
  const cadastro = useListaPermitida<Epi>('epi.read', 'Cadastro de EPIs', carregarCadastro);

  const [dialogo, setDialogo] = useState<Dialogo | null>(null);
  const [gravando, setGravando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const fechar = () => {
    setDialogo(null);
    setErro(null);
  };

  const gravar = async (acao: () => Promise<void>, falha: string) => {
    setGravando(true);
    setErro(null);
    try {
      await acao();
      setDialogo(null);
      daEmpresa.recarregar();
    } catch (err: unknown) {
      setErro(getApiErrorMessage(err, falha));
    } finally {
      setGravando(false);
    }
  };

  const epiAtivos = cadastro.itens;

  const rotuloDoCertificado = (certificado: CompanyEpiCertificate) =>
    `C.A. ${certificado.numero} · Validade ${formatarValidade(certificado.validade)}`;

  const renderCertificado = (epi: CompanyEpi, certificado: CompanyEpiCertificate) => (
    <Box key={certificado.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Typography variant="body2">{rotuloDoCertificado(certificado)}</Typography>
      {certificadoVencido(certificado.validade) && <Chip size="small" color="error" label="vencido" />}
      {!editar.disabled && (
        <IconButton
          size="small"
          aria-label={`Trocar C.A. ${certificado.numero}`}
          onClick={() => setDialogo({ tipo: 'certificado', epi, certificado })}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}
      {!remover.disabled && (
        <IconButton
          size="small"
          aria-label={`Remover C.A. ${certificado.numero}`}
          onClick={() => setDialogo({ tipo: 'remover-certificado', epi, certificado })}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  const renderEpi = (epi: CompanyEpi) => {
    const propria = epi.orientacaoDeUso !== null;
    const orientacao = propria ? epi.orientacaoDeUso : epi.orientacaoDoCadastro;
    return (
      <Box key={epi.id} component="article" sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {epi.epiNome}
          </Typography>
          <Chip size="small" variant="outlined" label={propria ? 'orientação própria' : 'do cadastro-base'} />
        </Box>

        <Stack spacing={0.5} sx={{ mb: 1 }}>
          {epi.certificates.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Nenhum C.A. cadastrado.
            </Typography>
          )}
          {epi.certificates.map((certificado) => renderCertificado(epi, certificado))}
        </Stack>

        {orientacao && (
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
            {orientacao}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {!adicionar.disabled && (
            <Button
              size="small"
              onClick={() => setDialogo({ tipo: 'certificado', epi, certificado: null })}
              aria-label={`Adicionar C.A. a ${epi.epiNome}`}
            >
              Adicionar C.A.
            </Button>
          )}
          {!editar.disabled && (
            <Button
              size="small"
              onClick={() => setDialogo({ tipo: 'orientacao', epi })}
              aria-label={`Editar orientação de ${epi.epiNome}`}
            >
              Editar orientação
            </Button>
          )}
          {!remover.disabled && (
            <Button
              size="small"
              color="error"
              onClick={() => setDialogo({ tipo: 'remover-epi', epi })}
              aria-label={`Remover EPI ${epi.epiNome}`}
            >
              Remover EPI
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  const conteudo = (): React.ReactNode => {
    if (!daEmpresa.resolvida) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    const falha = daEmpresa.bloqueio ?? daEmpresa.erro;
    if (falha) {
      return (
        <Typography variant="body2" color="text.secondary">
          {falha}
        </Typography>
      );
    }

    return (
      <>
        {erro && !dialogo && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>
            {erro}
          </Alert>
        )}

        {cadastro.resolvida && cadastro.permitido && epiAtivos.length === 0 && !adicionar.disabled && (
          <Box sx={{ mb: 2 }}>
            <PrerequisiteBlock
              title="Nenhum EPI cadastrado no sistema"
              reason="É do cadastro de EPIs que a empresa escolhe os que entrega."
              resolve={{
                label: 'Abrir cadastro de EPIs',
                permission: 'epi.create',
                mode: 'navigate',
                route: '/epis',
              }}
              deniedHint="Peça ao administrador da plataforma para cadastrar os EPIs."
            />
          </Box>
        )}

        {daEmpresa.itens.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhum EPI cadastrado para esta empresa. O anexo de EPI lista também os EPIs aplicados nos riscos.
          </Typography>
        ) : (
          daEmpresa.itens.map(renderEpi)
        )}
      </>
    );
  };

  const renderDialogo = (): React.ReactNode => {
    if (!dialogo) return null;
    switch (dialogo.tipo) {
      case 'adicionar-epi':
        return (
          <DialogoDeEpi
            epis={epiAtivos}
            ocupado={gravando}
            erro={erro}
            onFechar={fechar}
            onConfirmar={(epiId) => gravar(() => companyEpiService.add(companyId, epiId), 'Erro ao adicionar o EPI')}
          />
        );
      case 'certificado': {
        const { epi, certificado } = dialogo;
        return (
          <DialogoDeCertificado
            certificado={certificado}
            ocupado={gravando}
            erro={erro}
            onFechar={fechar}
            onConfirmar={(dados) =>
              gravar(
                () =>
                  certificado
                    ? companyEpiService.updateCertificate(String(epi.id), String(certificado.id), dados)
                    : companyEpiService.addCertificate(String(epi.id), dados),
                'Erro ao gravar o C.A.'
              )
            }
          />
        );
      }
      case 'orientacao': {
        const { epi } = dialogo;
        return (
          <DialogoDeOrientacao
            epi={epi}
            ocupado={gravando}
            erro={erro}
            onFechar={fechar}
            onConfirmar={(orientacao) => {
              const copiaDaBase = epi.orientacaoDeUso === null && orientacao === epi.orientacaoDoCadastro;
              if (copiaDaBase) {
                fechar();
                return;
              }
              void gravar(
                () => companyEpiService.updateOrientation(String(epi.id), orientacao),
                'Erro ao gravar a orientação'
              );
            }}
          />
        );
      }
      case 'remover-epi': {
        const { epi } = dialogo;
        return (
          <DialogoDeRemocao
            titulo={`Remover ${epi.epiNome} da empresa?`}
            ocupado={gravando}
            erro={erro}
            onFechar={fechar}
            onConfirmar={() => gravar(() => companyEpiService.remove(String(epi.id)), 'Erro ao remover o EPI')}
          />
        );
      }
      case 'remover-certificado': {
        const { epi, certificado } = dialogo;
        return (
          <DialogoDeRemocao
            titulo={`Remover o C.A. ${certificado.numero}?`}
            ocupado={gravando}
            erro={erro}
            onFechar={fechar}
            onConfirmar={() =>
              gravar(
                () => companyEpiService.removeCertificate(String(epi.id), String(certificado.id)),
                'Erro ao remover o C.A.'
              )
            }
          />
        );
      }
    }
  };

  const botaoAdicionar = !adicionar.disabled && (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      {cadastro.bloqueio && (
        <Typography variant="body2" color="text.secondary">
          {cadastro.bloqueio}
        </Typography>
      )}
      <Button
        size="small"
        variant="contained"
        startIcon={<AddIcon />}
        disabled={!cadastro.resolvida || epiAtivos.length === 0}
        onClick={() => setDialogo({ tipo: 'adicionar-epi' })}
      >
        Adicionar EPI
      </Button>
    </Box>
  );

  return (
    <ProfileSection title="EPIs da empresa" icon={<EpiIcon />} action={botaoAdicionar || undefined}>
      {conteudo()}
      {renderDialogo()}
    </ProfileSection>
  );
};

export default CompanyEpisSection;
